import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import questionsConfig from '../config/questions.json';

function EditResponses() {
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const questions = questionsConfig.questions;

  useEffect(() => {
    const isDevMode = localStorage.getItem('devMode') === 'true';
    if (!currentUser && !isDevMode) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSelectAll = (questionId, options) => {
    const allValues = options.map(option => option.value);
    setAnswers(prev => ({
      ...prev,
      [questionId]: allValues
    }));
  };

  const handleDeselectAll = (questionId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: []
    }));
  };

  const handleCheckboxGroupChange = (questionId, checkboxId, isChecked, textValue = '') => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || {};
      if (isChecked) {
        return {
          ...prev,
          [questionId]: {
            ...currentAnswers,
            [checkboxId]: textValue || true
          }
        };
      } else {
        const newAnswers = { ...currentAnswers };
        delete newAnswers[checkboxId];
        return {
          ...prev,
          [questionId]: newAnswers
        };
      }
    });
  };

  const isQuestionVisible = (question) => {
    if (!question.conditional) return true;
    
    const { dependsOn, showWhen } = question.conditional;
    const dependentAnswer = answers[dependsOn];
    
    return showWhen.includes(dependentAnswer);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const userName = localStorage.getItem('userName') || 'Unknown';
      const isDevMode = localStorage.getItem('devMode') === 'true';
      // Use consistent dev userId for dev mode so we can update existing responses
      const userId = isDevMode ? 'dev-user' : (currentUser?.uid || 'unknown');
      const phoneNumber = isDevMode ? '+1234567890' : (currentUser?.phoneNumber || 'Unknown');
      
      // Filter out epicPass question from answers (not saved to DB)
      const filteredAnswers = { ...answers };
      delete filteredAnswers.epicPass;
      
      const responseData = {
        userId: userId,
        userName: userName,
        phoneNumber: phoneNumber,
        answers: filteredAnswers,
        submittedAt: new Date().toISOString(),
        tripDates: questionsConfig.tripInfo.dates,
        devMode: isDevMode,
        isEdit: true
      };

      try {
        // Check if a response already exists for this user
        let existingDocId = null;
        if (userId && userId !== 'unknown') {
          const q = query(
            collection(db, 'responses'),
            where('userId', '==', userId)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            existingDocId = querySnapshot.docs[0].id;
          }
        }

        if (existingDocId) {
          // Update existing document
          await updateDoc(doc(db, 'responses', existingDocId), {
            ...responseData,
            lastUpdatedAt: new Date().toISOString()
          });
          console.log('✅ Updated existing response in Firestore:', existingDocId);
        } else {
          // Create new document
          await addDoc(collection(db, 'responses'), responseData);
          console.log('✅ Created new response in Firestore');
        }
      } catch (firestoreError) {
        console.warn('Firestore not available, saving locally:', firestoreError);
        
        // Fallback: save to localStorage
        const existingResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        
        // Check if response exists in localStorage
        const existingIndex = existingResponses.findIndex(r => 
          (r.userId === userId && r.phoneNumber === phoneNumber) || 
          (r.userId === userId) ||
          (r.phoneNumber === phoneNumber && !isDevMode)
        );

        if (existingIndex >= 0) {
          // Update existing response
          existingResponses[existingIndex] = {
            ...existingResponses[existingIndex],
            ...responseData,
            lastUpdatedAt: new Date().toISOString()
          };
          console.log('✅ Updated existing response in localStorage');
        } else {
          // Add new response
          existingResponses.push(responseData);
          console.log('✅ Created new response in localStorage');
        }
        localStorage.setItem('localResponses', JSON.stringify(existingResponses));
        
        // Also update adminData
        const existingAdminData = JSON.parse(localStorage.getItem('adminData') || '[]');
        const adminDataIndex = existingAdminData.findIndex(r => 
          (r.userId === userId && r.phoneNumber === phoneNumber) ||
          (r.userId === userId) ||
          (r.phoneNumber === phoneNumber && !isDevMode)
        );

        const adminData = {
          id: adminDataIndex >= 0 ? existingAdminData[adminDataIndex].id : 'local-' + Date.now(),
          ...responseData,
          submittedAt: new Date(responseData.submittedAt)
        };

        if (adminDataIndex >= 0) {
          existingAdminData[adminDataIndex] = adminData;
          console.log('✅ Updated existing adminData');
        } else {
          existingAdminData.push(adminData);
          console.log('✅ Created new adminData');
        }
        localStorage.setItem('adminData', JSON.stringify(existingAdminData));
      }
      
      setSaved(true);
      setTimeout(() => {
        navigate('/confirmation');
      }, 2000);
    } catch (error) {
      setError('Failed to save your responses. Please try again.');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    if (!isQuestionVisible(question)) return null;

    switch (question.type) {
      case 'singleSelect':
        return (
          <div>
            {question.id === 'transportation' ? (
              // Special handling for transportation to show seat input under the drive option
              <div>
                {question.options.map((option) => (
                  <div key={option.value} style={{ marginBottom: '15px' }}>
                    <label
                      className={`option-item ${answers[question.id] === option.value ? 'selected' : ''}`}
                      style={{ 
                        display: 'block',
                        marginBottom: option.value === 'drive' && answers[question.id] === 'drive' ? '10px' : '0'
                      }}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option.value}
                        checked={answers[question.id] === option.value}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        className="option-checkbox"
                      />
                      {option.label}
                    </label>
                    
                    {/* Show seat input if this is the drive option and it's selected */}
                    {option.value === 'drive' && answers[question.id] === 'drive' && (
                      <div style={{ 
                        padding: '15px',
                        background: '#f0f9ff',
                        borderRadius: '12px',
                        border: '1px solid #bae6fd',
                        animation: 'fadeIn 0.3s ease'
                      }}>
                        <label style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          color: '#0ea5e9',
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}>
                          How many additional seats do you have available? (excluding yourself)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={answers['transportation_seats'] || ''}
                          onChange={(e) => handleAnswerChange('transportation_seats', e.target.value === '' ? '' : parseInt(e.target.value))}
                          placeholder="0"
                          style={{
                            width: '100px',
                            padding: '8px 12px',
                            border: '2px solid #bae6fd',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Regular singleSelect rendering for other questions
              <div className="option-group">
                {question.options.map((option) => (
                  <label
                    key={option.value}
                    className={`option-item ${answers[question.id] === option.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={option.value}
                      checked={answers[question.id] === option.value}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className="option-checkbox"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        );

      case 'epicPass':
        return (
          <div>
            <div className="option-group">
              {question.options.map((option) => (
                <label
                  key={option.value}
                  className={`option-item ${answers[question.id] === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={answers[question.id] === option.value}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="option-checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {/* Show pass purchase info if they don't have a pass */}
            {answers[question.id] === 'no' && question.noPassInfo && (
              <div style={{ 
                marginTop: '20px',
                padding: '20px',
                background: 'linear-gradient(135deg, #e6f3ff 0%, #b3d9ff 100%)',
                borderRadius: '12px',
                border: '2px solid #4a90e2',
                animation: 'fadeIn 0.3s ease'
              }}>
                <h4 style={{ 
                  margin: '0 0 12px 0',
                  color: '#2c5282',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {question.noPassInfo.title}
                </h4>
                <p style={{ 
                  margin: '0 0 16px 0',
                  color: '#2d3748',
                  fontSize: '0.95rem'
                }}>
                  {question.noPassInfo.description}
                </p>
                <ul style={{ 
                  margin: '0 0 16px 0',
                  paddingLeft: '20px',
                  color: '#2d3748'
                }}>
                  {question.noPassInfo.info.map((item, index) => (
                    <li key={index} style={{ marginBottom: '4px', fontSize: '0.9rem' }}>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={question.noPassInfo.purchaseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(74, 144, 226, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(74, 144, 226, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(74, 144, 226, 0.3)';
                  }}
                >
                  {question.noPassInfo.linkText} →
                </a>
              </div>
            )}
          </div>
        );

      case 'multiSelect':
        return (
          <div>
            {question.showSelectAll && (
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '15px',
                justifyContent: 'center'
              }}>
                <button
                  type="button"
                  onClick={() => handleSelectAll(question.id, question.options)}
                  style={{
                    background: '#e6fffa',
                    border: '2px solid #81e6d9',
                    color: '#319795',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => handleDeselectAll(question.id)}
                  style={{
                    background: '#fed7d7',
                    border: '2px solid #feb2b2',
                    color: '#c53030',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
            <div className="option-group">
              {question.options.map((option) => (
                <label
                  key={option.value}
                  className={`option-item ${(answers[question.id] || []).includes(option.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(answers[question.id] || []).includes(option.value)}
                    onChange={(e) => {
                      const currentAnswers = answers[question.id] || [];
                      if (e.target.checked) {
                        handleAnswerChange(question.id, [...currentAnswers, option.value]);
                      } else {
                        handleAnswerChange(question.id, currentAnswers.filter(a => a !== option.value));
                      }
                    }}
                    className="option-checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {/* Show text input if "other" is selected for accommodation */}
            {question.id === 'accommodation' && (answers[question.id] || []).includes('other') && (
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                background: '#f0f9ff',
                borderRadius: '12px',
                border: '1px solid #bae6fd',
                animation: 'fadeIn 0.3s ease'
              }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  color: '#0ea5e9',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}>
                  Please specify:
                </label>
                <input
                  type="text"
                  value={answers['accommodation_other'] || ''}
                  onChange={(e) => handleAnswerChange('accommodation_other', e.target.value)}
                  placeholder="e.g., Pet-friendly, elevator access, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #bae6fd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>
            )}
          </div>
        );

      case 'slider':
        const currentValue = answers[question.id] || question.default;
        const isAtMax = currentValue >= question.max;
        
        return (
          <div className="slider-container">
            {question.showLabels && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '10px',
                fontSize: '0.9rem',
                color: '#718096'
              }}>
                <span>{question.minLabel || `$${question.min}`}</span>
                <span>{question.maxLabel || `$${question.max}`}</span>
              </div>
            )}
            <input
              type="range"
              min={question.min}
              max={question.max}
              step={question.step}
              value={currentValue}
              onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
              className="slider"
            />
            <div className="slider-value">
              ${currentValue}
            </div>
            
            {/* Max value checkbox */}
            {question.maxValueCheckbox?.show && isAtMax && (
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                background: 'linear-gradient(135deg, #fef5e7 0%, #f6e05e 100%)',
                borderRadius: '12px',
                border: '2px solid #f6e05e',
                animation: 'fadeIn 0.3s ease'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  margin: 0
                }}>
                  <input
                    type="checkbox"
                    checked={answers[question.id + '_maxCheckbox'] || false}
                    onChange={(e) => handleAnswerChange(question.id + '_maxCheckbox', e.target.checked)}
                    style={{ 
                      marginRight: '12px',
                      width: '18px',
                      height: '18px',
                      accentColor: '#d69e2e'
                    }}
                  />
                  <div>
                    <div style={{ 
                      fontWeight: '700',
                      color: '#d69e2e',
                      fontSize: '1.1rem',
                      marginBottom: '4px'
                    }}>
                      {question.maxValueCheckbox.label}
                    </div>
                    <div style={{ 
                      color: '#b7791f',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      {question.maxValueCheckbox.description}
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        );

      case 'checkboxGroup':
        return (
          <div className="option-group">
            {question.options.map((option) => {
              const isChecked = answers[question.id]?.[option.id];
              const textValue = typeof isChecked === 'string' ? isChecked : '';
              
              return (
                <div key={option.id} style={{ marginBottom: '15px' }}>
                  <label className="option-item" style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    padding: '16px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: isChecked ? '#f0f9ff' : '#f7fafc',
                    borderColor: isChecked ? '#0ea5e9' : '#e2e8f0'
                  }}>
                    <input
                      type="checkbox"
                      checked={!!isChecked}
                      onChange={(e) => handleCheckboxGroupChange(
                        question.id, 
                        option.id, 
                        e.target.checked,
                        textValue
                      )}
                      className="option-checkbox"
                      style={{ marginRight: '12px' }}
                    />
                    <span style={{ 
                      fontWeight: isChecked ? '600' : '400',
                      color: isChecked ? '#0ea5e9' : '#4a5568'
                    }}>
                      {option.label}
                    </span>
                  </label>
                  
                  {option.hasTextInput && isChecked && (
                    <div style={{ 
                      marginTop: '10px', 
                      marginLeft: '32px',
                      animation: 'fadeIn 0.3s ease'
                    }}>
                      <textarea
                        className="form-input"
                        value={textValue}
                        onChange={(e) => handleCheckboxGroupChange(
                          question.id, 
                          option.id, 
                          true,
                          e.target.value
                        )}
                        placeholder={option.textInputPlaceholder}
                        rows="3"
                        style={{ 
                          resize: 'vertical', 
                          minHeight: '80px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'text':
        return (
          <textarea
            className="form-input"
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows="4"
            style={{ resize: 'vertical', minHeight: '100px' }}
          />
        );

      default:
        return null;
    }
  };

  const isDevMode = localStorage.getItem('devMode') === 'true';
  if (!currentUser && !isDevMode) {
    return <div className="loading">Loading...</div>;
  }

  if (saved) {
    return (
      <div className="container">
        <div className="card">
          <div className="card-header">
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>✅</div>
            <h1 className="card-title">Responses Updated!</h1>
            <p className="card-subtitle">
              Your changes have been saved successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">✏️ Edit Your Responses</h1>
          <p className="card-subtitle">
            Update your answers below and click Save when you're done.
          </p>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="scrollable-area" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>
          {questions.map((question, index) => (
            <div key={question.id} className="question-card" style={{ marginBottom: '30px' }}>
              <h3 className="question-title" style={{ fontSize: '1.2rem' }}>
                {index + 1}. {question.title}
              </h3>
              {question.description && (
                <p className="question-description" style={{ fontSize: '0.9rem' }}>
                  {question.description}
                </p>
              )}
              
              {renderQuestion(question)}
            </div>
          ))}
        </div>

        <div className="sticky-save-container">
          <div className="navigation">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/confirmation')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditResponses;
